import React, { useState, useEffect } from 'react'
import st from './MassEditDealsForm.module.scss'
import Button from 'components/CRM/UI/components/Button/Button'
import Loader from 'components/UI/Loader/Loader'
import swal from 'sweetalert'
import { superAxiosRequest } from 'axiosApi'
import SelectCustom from 'components/ContentBox/Global/Select/SelectCustom'
import FormDateSelector from 'components/UI/AsideModal/Form/FormDateSelector'
import FormField from 'components/UI/AsideModal/Form/FormField'
import FormLabel from 'components/UI/AsideModal/Form/FormLabel'
import FormInput from 'components/UI/AsideModal/Form/FormInput'
import { timeConverter2 } from '../../../ContentBox/Online-schedule/components/functions'
import TypeSelector from '../../../ContentBox/Global/GlobalModalSingle/templates/WorkPlain/TypeSelector'

const MassEditDealsForm = ({
  dealsForEdit,
  onClose,
  onSave,
  translate,
  funnelId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(true)
  const [selectedDeals, setSelectedDeals] = useState([])

  // Состояния для полей
  const [editValues, setEditValues] = useState({})
  const [fetchRequireFields, setFetchRequireFields] = useState({
    manager: [],
  })

  // Состояния для кастомных полей
  const [customFields, setCustomFields] = useState([])
  const [customFieldsValues, setCustomFieldsValues] = useState([])

  // Загрузка обязательных полей (менеджеры)
  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        const managerResponse = await superAxiosRequest({
          link: 'integration/employees',
          method: 'get',
          crm: true,
          params: { limit: 9999 },
        })

        if (isMounted) {
          const responsibleOptions = managerResponse.data.result.map(
            (responsible) => ({
              id: responsible.id,
              name: responsible.full_name,
            }),
          )

          setFetchRequireFields({
            manager: responsibleOptions,
          })
        }
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [])

  // Загрузка кастомных полей воронки
  useEffect(() => {
    if (!funnelId) return

    const fetchCustomFields = async () => {
      try {
        const response = await superAxiosRequest({
          link: 'integration/additional_funnel_deal_fields',
          method: 'get',
          crm: true,
          params: { funnel_id: funnelId },
        })
        setCustomFields(response.data || [])
      } catch (error) {
        console.error('Ошибка загрузки кастомных полей:', error)
      }
    }

    fetchCustomFields()
  }, [funnelId])

  // Инициализация выбранных сделок
  useEffect(() => {
    if (dealsForEdit.length) {
      setSelectedDeals(dealsForEdit.map((deal) => ({ ...deal })))
    }
    setLoadingData(false)
  }, [dealsForEdit])

  // Удаление сделки из списка
  const removeDeal = (dealId) => {
    if (selectedDeals.length <= 2) {
      swal('Внимание!', 'Должно быть выбрано минимум 2 сделки', 'warning')
      return
    }
    setSelectedDeals((prev) => prev.filter((deal) => deal.id !== dealId))
  }

  // Обработчик изменения стандартного поля
  const handleFieldChange = (fieldName, value) => {
    let finalValue = value

    // Для полей-объектов (select) берем id
    if (value && typeof value === 'object' && value.id) {
      finalValue = value.id
    }

    // Для бюджета форматируем
    if (fieldName === 'budget' && value) {
      finalValue = parseFloat(value).toFixed(2)
    }

    setEditValues((prev) => {
      const newValues = { ...prev }

      // Если поле очищено, удаляем его из состояния, чтобы не отправлять null на бэкенд
      if (
        finalValue === null ||
        finalValue === '' ||
        finalValue === undefined
      ) {
        delete newValues[fieldName]
      } else {
        newValues[fieldName] = finalValue
      }

      // ВАЖНО: Если меняется воронка (или очищается), сбрасываем выбранный статус,
      // так как статусы принадлежат конкретной воронке и старый ID будет невалиден
      if (fieldName === 'to_funnel_id') {
        delete newValues.to_status_deal_id
      }

      return newValues
    })
  }

  // Обработчик изменения кастомного поля
  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldsValues((prev) => {
      const existing = prev.find((v) => v.id === fieldId)
      if (existing) {
        return prev.map((v) => (v.id === fieldId ? { ...v, value } : v))
      } else {
        return [...prev, { id: fieldId, value }]
      }
    })
  }

  // Рендер кастомного поля
  const renderCustomField = (field) => {
    const fieldValue =
      customFieldsValues.find((v) => v.id === field.id)?.value || ''

    return (
      <div key={field.id}>
        {field.type === 'string' && (
          <FormField>
            <FormLabel>{field?.name}:</FormLabel>
            <FormInput>
              <input
                type='text'
                value={fieldValue}
                onChange={(e) =>
                  handleCustomFieldChange(field.id, e.target.value)
                }
                placeholder={field?.name}
              />
            </FormInput>
          </FormField>
        )}

        {field.type === 'calendar' && (
          <>
            <FormLabel>{field?.name}:</FormLabel>
            <FormDateSelector
              placeholder='Выберите дату'
              onChange={(date) => {
                const value = date ? timeConverter2(date) : ''
                handleCustomFieldChange(field.id, value)
              }}
              value={fieldValue || ''}
            />
          </>
        )}

        {field.type === 'boolean' && (
          <FormField>
            <FormLabel>{field?.name}:</FormLabel>
            <TypeSelector
              values={[
                { id: true, name: 'Да' },
                { id: false, name: 'Нет' },
              ]}
              like_obj={true}
              objectChangeHandler={(value) =>
                handleCustomFieldChange(field.id, value)
              }
              selected={fieldValue}
              name={field.id}
            />
          </FormField>
        )}
      </div>
    )
  }

  // Получение названия поля для отображения
  const getFieldLabel = (fieldName) => {
    const fieldMap = {
      manager: 'Ответственный',
      budget: 'Бюджет',
      city: 'Город',
      branch: 'Филиал',
      program: 'Программа',
      level: 'Уровень',
      source: 'Источник',
      learning_days: 'Дни обучения',
      learning_goal: 'Цель обучения',
      learning_type: 'Тип обучения',
      reject_reason: 'Причина отказа',
      education_type: 'Тип образования',
      to_status_deal_id: 'Статус сделки',
      to_funnel_id: 'Воронка',
    }
    return fieldMap[fieldName] || fieldName
  }

  // Получение читаемого значения для отображения
  const getDisplayValue = (fieldName, value) => {
    if (!value && value !== 0 && value !== false) return 'Не выбрано'

    if (fieldName === 'budget') return `${value} ₸`

    if (fieldName === 'manager') {
      const manager = fetchRequireFields.manager.find((m) => m.id === value)
      return manager?.name || value
    }

    return value
  }

  // Сохранение изменений
  const handleSave = async () => {
    if (isLoading) return

    if (selectedDeals.length < 2) {
      return swal('Ошибка!', 'Должно быть выбрано минимум 2 сделки', 'warning')
    }

    if (
      Object.keys(editValues).length === 0 &&
      customFieldsValues.length === 0
    ) {
      return swal(
        'Ошибка!',
        'Выберите хотя бы одно поле для изменения',
        'warning',
      )
    }

    if (editValues.to_funnel_id && !editValues.to_status_deal_id) {
      return swal(
        'Ошибка!',
        'При выборе воронки необходимо обязательно указать статус сделки',
        'warning',
      )
    }

    // Дополнительная проверка: если статус выбран, а воронка нет
    if (editValues.to_status_deal_id && !editValues.to_funnel_id) {
      return swal(
        'Ошибка!',
        'Для изменения статуса сделки необходимо выбрать воронку',
        'warning',
      )
    }

    setIsLoading(true)

    try {
      const { to_status_deal_id, to_funnel_id, ...otherData } = editValues

      const payload = {
        deal_ids: selectedDeals.map((deal) => deal.id),
        data: otherData,
        additional_funnel_fields: customFieldsValues.map((field) => ({
          id: field.id,
          value: field.value,
        })),
      }

      if (to_status_deal_id) {
        payload.to_status_deal_id = to_status_deal_id
      }

      if (to_funnel_id) {
        payload.to_funnel_id = to_funnel_id
      }

      if (funnelId && to_funnel_id) {
        payload.from_funnel_id = funnelId
      }

      await onSave(payload)
    } catch (error) {
      console.error('Ошибка при массовом обновлении:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Вычисляем ID воронки, для которой нужно показать статусы:
  const targetFunnelIdForStatuses = editValues.to_funnel_id || funnelId

  return (
    <div className={st.mergeModalOverlay}>
      <div className={st.mergeModal}>
        <div className={st.mergeModalHeader}>
          <h2>Массовые изменения ({dealsForEdit.length})</h2>
          <button className={st.closeButton} onClick={onClose}>
            x{' '}
          </button>
        </div>

        <div className={st.mergeModalContent}>
          {loadingData ? (
            <div className={st.loadingContainer}>
              <Loader />
              <p>Загрузка данных...</p>
            </div>
          ) : (
            <>
              <div className={st.mergeInstructions}>
                <p>
                  Выберите поля, которые хотите изменить у всех выбранных
                  сделок.
                </p>
              </div>

              {/* Список выбранных сделок */}
              <div className={st.selectedDealsSection}>
                <h3>Выбранные сделки:</h3>
                <div className={st.dealsList}>
                  {selectedDeals.map((deal, index) => (
                    <React.Fragment key={deal.id}>
                      <span className={st.dealItem}>
                        <span className={st.dealName}>
                          {deal.deal_name || `Сделка #${deal.id}`}
                          {deal.client?.name && ` (${deal.client.name})`}
                        </span>
                        <button
                          className={st.removeDealBtn}
                          onClick={() => removeDeal(deal.id)}
                          title='Удалить из списка'
                        >
                          x{' '}
                        </button>
                      </span>
                      {index < selectedDeals.length - 1 && (
                        <span className={st.dealSeparator}>,</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Форма редактирования */}
              <div className={st.editForm}>
                <div className={st.fieldsGrid}>
                  {/* Ответственный */}
                  <FormField>
                    <FormLabel>Ответственный:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите ответственного'
                      options={fetchRequireFields.manager}
                      sortKey='name'
                      valueID={editValues.manager}
                      onChange={(value) => handleFieldChange('manager', value)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Бюджет */}
                  <FormField>
                    <FormLabel>Бюджет, ₸:</FormLabel>
                    <FormInput>
                      <input
                        type='number'
                        value={editValues.budget || ''}
                        onChange={(e) =>
                          handleFieldChange('budget', e.target.value)
                        }
                        placeholder='Введите бюджет'
                      />
                    </FormInput>
                  </FormField>

                  {/* Город */}
                  <FormField>
                    <FormLabel>Город:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите город'
                      apiLinkCRM='integration/cities'
                      apiConfParams={{ limit: 99 }}
                      sortKey='name'
                      valueID={editValues.city}
                      onChange={(value) => handleFieldChange('city', value)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Филиал */}
                  <FormField>
                    <FormLabel>Филиал:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите филиал'
                      apiLinkCRM='integration/branches'
                      sortKey='name'
                      valueID={editValues.branch}
                      onChange={(value) => handleFieldChange('branch', value)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Программа */}
                  <FormField>
                    <FormLabel>Программа:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите программу'
                      apiLinkCRM='integration/programs'
                      sortKey='name'
                      valueID={editValues.program}
                      onChange={(value) => handleFieldChange('program', value)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Уровень */}
                  <FormField>
                    <FormLabel>Уровень:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите уровень'
                      apiLinkCRM='integration/teaching_levels'
                      sortKey='name'
                      valueID={editValues.level}
                      onChange={(value) => handleFieldChange('level', value)}
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Источник */}
                  <FormField>
                    <FormLabel>Источник:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите источник'
                      apiLinkCRM='settings/project_settings/deal_source_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.source}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange('source', value?.name || value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Дни обучения */}
                  <FormField>
                    <FormLabel>Дни обучения:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите дни обучения'
                      apiLinkCRM='settings/project_settings/deal_learning_days_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.learning_days}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange('learning_days', value?.name || value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Цель обучения */}
                  <FormField>
                    <FormLabel>Цель обучения:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите цель обучения'
                      apiLinkCRM='settings/project_settings/deal_learning_goal_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.learning_goal}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange('learning_goal', value?.name || value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Тип обучения */}
                  <FormField>
                    <FormLabel>Тип обучения:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите тип обучения'
                      apiLinkCRM='settings/project_settings/deal_learning_type_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.learning_type}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange('learning_type', value?.name || value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Причина отказа */}
                  <FormField>
                    <FormLabel>Причина отказа:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите причину отказа'
                      apiLinkCRM='settings/project_settings/deal_reject_reason_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.reject_reason}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange('reject_reason', value?.name || value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Тип образования */}
                  <FormField>
                    <FormLabel>Тип образования:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите тип образования'
                      apiLinkCRM='settings/project_settings/deal_education_type_choices'
                      apiGetResult={(result) => result.value?.choices || result}
                      valueID={editValues.education_type}
                      optionLabelKey='name'
                      optionValueKey='name'
                      onChange={(value) =>
                        handleFieldChange(
                          'education_type',
                          value?.name || value,
                        )
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Воронка */}
                  <FormField>
                    <FormLabel>Воронка:</FormLabel>
                    <SelectCustom
                      placeholder='Выберите воронку'
                      apiLinkCRM='integration/funnels'
                      apiConfParams={{ my_onlyfunns: true }}
                      sortKey='name'
                      valueID={editValues.to_funnel_id}
                      onChange={(value) =>
                        handleFieldChange('to_funnel_id', value)
                      }
                      isSearchable
                      isClearable
                    />
                  </FormField>

                  {/* Статус сделки (Зависимый от выбранной воронки) */}
                  <FormField>
                    <FormLabel>
                      {' '}
                      {editValues.to_funnel_id && (
                        <span style={{ color: 'red' }}>*</span>
                      )}{' '}
                      Статус сделки:
                    </FormLabel>
                    <SelectCustom
                      // Ключ заставляет компонент полностью пересоздаться и сделать новый запрос при смене воронки
                      key={`deal-statuses-${targetFunnelIdForStatuses}`}
                      placeholder='Выберите статус сделки'
                      apiLinkCRM='integration/deal_statuses'
                      apiConfParams={{ funnel_id: targetFunnelIdForStatuses }}
                      sortKey='name'
                      valueID={editValues.to_status_deal_id}
                      onChange={(value) =>
                        handleFieldChange('to_status_deal_id', value)
                      }
                      apiGetResult={(result) => {
                        if (
                          result &&
                          typeof result === 'object' &&
                          !Array.isArray(result)
                        ) {
                          return Object.values(result)
                        }
                        return result || []
                      }}
                      isSearchable
                      isClearable
                      isDisabled={!editValues.to_funnel_id}
                    />
                  </FormField>
                </div>

                {/* Кастомные поля */}
                {customFields.length > 0 && (
                  <>
                    <div className={st.customFieldsGrid}>
                      {customFields.map((field) => renderCustomField(field))}
                    </div>
                  </>
                )}

                {/* Информация о выбранных полях */}
                {(Object.keys(editValues).length > 0 ||
                  customFieldsValues.length > 0) && (
                  <div className={st.selectedFieldsInfo}>
                    <h4>Будут изменены следующие поля:</h4>
                    <div className={st.selectedFieldsList}>
                      {/* Стандартные поля */}
                      {Object.entries(editValues).map(([key, value]) => (
                        <div key={key} className={st.selectedFieldItem}>
                          <span className={st.fieldLabel}>
                            {getFieldLabel(key)}:
                          </span>
                          <span className={st.fieldValue}>
                            {getDisplayValue(key, value)}
                          </span>
                        </div>
                      ))}

                      {/* Кастомные поля */}
                      {customFieldsValues.map((field) => {
                        const customField = customFields.find(
                          (f) => f.id === field.id,
                        )
                        let displayValue = field.value

                        if (typeof field.value === 'boolean') {
                          displayValue = field.value ? 'Да' : 'Нет'
                        } else if (!field.value && field.value !== 0) {
                          displayValue = 'Не выбрано'
                        }

                        return (
                          <div key={field.id} className={st.selectedFieldItem}>
                            <span className={st.fieldLabel}>
                              {customField?.name}:
                            </span>
                            <span className={st.fieldValue}>
                              {displayValue}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                    <p className={st.selectedFieldsNote}>
                      *Изменения применятся ко всем {selectedDeals.length}{' '}
                      выбранным сделкам
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={st.mergeActions}>
          <Button
            text='Отмена'
            onClick={onClose}
            className={st.cancelButton}
            isDisabled={isLoading}
          />
          <Button
            text={isLoading ? 'Сохранение...' : 'Применить изменения'}
            onClick={handleSave}
            className={st.mergeButton}
          />
        </div>
      </div>
    </div>
  )
}

export default MassEditDealsForm
